import React, { useState } from 'react';
import { useAnalytics } from '../recs/analytics';
import './SignalsPanel.css';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatClock(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SignalsPanel({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false);
  const {
    session,
    elapsedSeconds,
    impressions,
    clicks,
    dwells,
    playbackEvents,
    titlesSeenCount,
    mouseTrailEnabled,
    setMouseTrailEnabled,
  } = useAnalytics();

  return (
    <div className={`signals-panel ${open ? 'signals-panel--open' : ''}`}>
      <button className="signals-panel__toggle" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide signals' : 'Signals captured'}
      </button>

      {open && (
        <div className="signals-panel__body">
          <p className="signals-panel__note">
            Mockup of what the platform would capture in production — impressions, clicks, hover-dwell, playback,
            searches, and session/device context. Batched to S3 via the just_watch API every few seconds when it's
            reachable.
          </p>

          <section className="signals-panel__section">
            <h3>Session</h3>
            <dl className="signals-panel__grid">
              <dt>User</dt>
              <dd>{userId}</dd>
              <dt>Device</dt>
              <dd>{session.device}</dd>
              <dt>Network</dt>
              <dd>{session.network}</dd>
              <dt>Duration</dt>
              <dd>{formatElapsed(elapsedSeconds)}</dd>
              <dt>Titles seen</dt>
              <dd>{titlesSeenCount}</dd>
            </dl>
          </section>

          <section className="signals-panel__section">
            <label className="signals-panel__toggle-row">
              <input
                type="checkbox"
                checked={mouseTrailEnabled}
                onChange={(e) => setMouseTrailEnabled(e.target.checked)}
              />
              Show cursor trail
            </label>
          </section>

          <section className="signals-panel__section">
            <h3>Recent impressions</h3>
            {impressions.length === 0 ? (
              <p className="signals-panel__empty">Scroll a row to log one.</p>
            ) : (
              <ul className="signals-panel__feed">
                {impressions.map((e, i) => (
                  <li key={`${e.timestamp}-${i}`}>
                    <span className="signals-panel__feed-time">{formatClock(e.timestamp)}</span>
                    <span className="signals-panel__feed-title">{e.titleName}</span>
                    <span className="signals-panel__feed-meta">
                      {e.surface} · pos {e.rowPosition}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="signals-panel__section">
            <h3>Recent clicks</h3>
            {clicks.length === 0 ? (
              <p className="signals-panel__empty">Click a title to log one.</p>
            ) : (
              <ul className="signals-panel__feed">
                {clicks.map((e, i) => (
                  <li key={`${e.timestamp}-${i}`}>
                    <span className="signals-panel__feed-time">{formatClock(e.timestamp)}</span>
                    <span className="signals-panel__feed-title">{e.titleName}</span>
                    <span className="signals-panel__feed-meta">
                      {e.surface} · pos {e.rowPosition}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="signals-panel__section">
            <h3>Recent hovers</h3>
            {dwells.length === 0 ? (
              <p className="signals-panel__empty">Hover a title card for a moment to log one.</p>
            ) : (
              <ul className="signals-panel__feed">
                {dwells.map((e, i) => (
                  <li key={`${e.timestamp}-${i}`}>
                    <span className="signals-panel__feed-time">{formatClock(e.timestamp)}</span>
                    <span className="signals-panel__feed-title">{e.titleName}</span>
                    <span className="signals-panel__feed-meta">{(e.durationMs / 1000).toFixed(1)}s dwell</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="signals-panel__section">
            <h3>Playback events</h3>
            {playbackEvents.length === 0 ? (
              <p className="signals-panel__empty">Open a title and press Play to log one.</p>
            ) : (
              <ul className="signals-panel__feed">
                {playbackEvents.map((e, i) => (
                  <li key={`${e.timestamp}-${i}`}>
                    <span className="signals-panel__feed-time">{formatClock(e.timestamp)}</span>
                    <span className="signals-panel__feed-title">{e.titleName}</span>
                    <span className="signals-panel__feed-meta">
                      {e.action} at {e.elapsedSeconds}s
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
